import { useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDateISO } from '../lib/dateUtils';

interface Session {
  id: string;
  session_date: string;
  aircraft_type: string;
  flight_phase: string;
  status: 'draft' | 'in_progress' | 'completed' | 'approved' | 'failed';
  trainee: {
    full_name: string;
    email: string;
  } | null;
  instructor: {
    full_name: string;
  } | null;
  created_at: string;
}

interface SessionListProps {
  sessions: Session[];
  onEdit?: (sessionId: string) => void;
  onDelete?: (sessionId: string) => void;
}

type SortColumn = 'date' | 'trainee' | 'aircraft' | 'flight_phase' | 'instructor' | 'status';

interface ColumnDef {
  key: SortColumn;
  label: string;
  className: string;
  defaultSortDirection?: 'asc' | 'desc';
}

const COLUMNS: ColumnDef[] = [
  { key: 'trainee', label: 'Trainee', className: 'w-[22%] min-w-[140px]' },
  { key: 'date', label: 'Date', className: 'w-[12%] min-w-[110px]', defaultSortDirection: 'desc' },
  { key: 'aircraft', label: 'Aircraft', className: 'w-[11%] min-w-[100px]' },
  { key: 'flight_phase', label: 'Flight Phase', className: 'w-[15%] min-w-[130px]' },
  { key: 'instructor', label: 'Instructor', className: 'w-[15%] min-w-[120px]' },
  { key: 'status', label: 'Status', className: 'w-[10%] min-w-[100px]' },
];

export default function SessionList({ sessions, onEdit, onDelete }: SessionListProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<SortColumn>('date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const filteredSessions = sessions.filter((session) => {
    const matchesSearch =
      searchTerm === '' ||
      (session.trainee?.full_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      session.aircraft_type.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'all' || session.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const sortedSessions = [...filteredSessions].sort((a, b) => {
    const dir = sortDirection === 'asc' ? 1 : -1;
    switch (sortBy) {
      case 'date':
        return dir * (new Date(a.session_date).getTime() - new Date(b.session_date).getTime());
      case 'trainee':
        return dir * (a.trainee?.full_name || '').localeCompare(b.trainee?.full_name || '');
      case 'aircraft':
        return dir * a.aircraft_type.localeCompare(b.aircraft_type);
      case 'flight_phase':
        return dir * a.flight_phase.localeCompare(b.flight_phase);
      case 'instructor':
        return dir * (a.instructor?.full_name || '').localeCompare(b.instructor?.full_name || '');
      case 'status':
        return dir * a.status.localeCompare(b.status);
      default:
        return 0;
    }
  });

  const handleSort = (column: SortColumn) => {
    if (sortBy === column) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      const colDef = COLUMNS.find((c) => c.key === column);
      setSortDirection(colDef?.defaultSortDirection ?? 'asc');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft':
        return 'bg-steel/20 text-slate';
      case 'in_progress':
        return 'bg-yellow-100 text-yellow-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-steel/20 text-slate';
    }
  };

  const SortChevron = ({ column }: { column: SortColumn }) => {
    if (sortBy !== column) return <span className="ml-1 text-steel/30">&#x21C5;</span>;
    return (
      <span className="ml-1 text-orca-elevate">
        {sortDirection === 'asc' ? '\u25B2' : '\u25BC'}
      </span>
    );
  };

  const totalColumns = COLUMNS.length + 1; // +1 for actions

  return (
    <div className="space-y-3">
      {/* Toolbar: search + status filter */}
      <div className="flex flex-col md:flex-row gap-3">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Search by trainee, aircraft..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-3 py-2 bg-white border border-steel/30 rounded-md placeholder-steel/50 text-navy text-sm focus:outline-none focus:ring-2 focus:ring-orca-elevate focus:border-orca-elevate"
          />
        </div>
        <div className="w-full md:w-48">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full px-3 py-2 bg-white border border-steel/30 rounded-md text-navy text-sm focus:outline-none focus:ring-2 focus:ring-orca-elevate focus:border-orca-elevate"
          >
            <option value="all">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="approved">Approved</option>
            <option value="failed">Failed</option>
          </select>
        </div>
      </div>

      {/* Results count */}
      <div className="text-xs text-steel">
        Showing {sortedSessions.length} of {sessions.length} sessions
      </div>

      {/* Table */}
      <div className="border border-steel/15 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#0B2C45]/[0.04]">
                {COLUMNS.map((col) => (
                  <th
                    key={col.key}
                    className={`${col.className} px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider select-none cursor-pointer transition-colors hover:text-orca-elevate ${
                      sortBy === col.key ? 'text-orca-elevate' : 'text-navy/70'
                    }`}
                    onClick={() => handleSort(col.key)}
                  >
                    {col.label}
                    <SortChevron column={col.key} />
                  </th>
                ))}
                <th className="w-[15%] min-w-[160px] px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-navy/70">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedSessions.length === 0 ? (
                <tr>
                  <td colSpan={totalColumns} className="text-center py-16">
                    <p className="text-steel">No sessions found</p>
                    <p className="text-xs text-steel/60 mt-1">
                      Try adjusting your search or filters
                    </p>
                  </td>
                </tr>
              ) : (
                sortedSessions.map((session) => (
                  <tr
                    key={session.id}
                    className="bg-white border-b border-steel/10 last:border-b-0 hover:bg-[#1F4FFF]/[0.025] transition-colors"
                  >
                    {/* Trainee */}
                    <td className="px-4 py-3">
                      <Link
                        to={`/sessions/${session.id}`}
                        className="font-semibold text-navy hover:text-orca-elevate transition-colors"
                      >
                        {session.trainee?.full_name || 'No Trainee'}
                      </Link>
                    </td>

                    {/* Date */}
                    <td className="px-4 py-3 text-steel tabular-nums">
                      {formatDateISO(session.session_date)}
                    </td>

                    {/* Aircraft */}
                    <td className="px-4 py-3 text-steel">
                      {session.aircraft_type}
                    </td>

                    {/* Flight Phase */}
                    <td className="px-4 py-3 text-steel">
                      {session.flight_phase}
                    </td>

                    {/* Instructor */}
                    <td className="px-4 py-3 text-steel">
                      {session.instructor?.full_name || 'No Instructor'}
                    </td>

                    {/* Status */}
                    <td className="px-4 py-3">
                      <span
                        className={`inline-block px-2.5 py-0.5 text-[11px] font-semibold rounded-full whitespace-nowrap ${getStatusColor(session.status)}`}
                      >
                        {session.status.replace('_', ' ').toUpperCase()}
                      </span>
                    </td>

                    {/* Actions */}
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Link
                          to={`/sessions/${session.id}`}
                          className="px-2 py-1 text-xs font-medium text-orca-elevate hover:underline transition-colors"
                        >
                          View
                        </Link>
                        <Link
                          to={`/session-workflow/${session.id}`}
                          className="px-2 py-1 text-xs font-medium text-navy hover:underline transition-colors"
                          title="Start recording workflow"
                        >
                          Record
                        </Link>
                        {onEdit && (
                          <button
                            onClick={() => onEdit(session.id)}
                            className="px-2 py-1 text-xs font-medium text-steel hover:text-navy hover:underline transition-colors"
                          >
                            Edit
                          </button>
                        )}
                        {onDelete && (
                          <button
                            onClick={() => onDelete(session.id)}
                            className="px-2 py-1 text-xs font-medium text-red-500 hover:text-red-700 hover:underline transition-colors"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
